import { PriceAlert } from '../services/priceAlerts.ts/priceAlertsService';
import { View } from '../components/orderhistory/types';
import { startOfPeriod } from '../utils/dateUtils';
import { OrderHistoryState } from '../stores/appOrderHistoryStore';
import { resolveProductIdentifier } from './platformUtils';
import { resolveSpendSide } from './spendSideUtils';
import { OverallTypeFilter, PriceAlertChangeType } from '../stores/appFiltersStore';

/**
 * Locate the product an alert refers to inside an order's line items. Resolves BOTH sides through
 * `resolveProductIdentifier` so the alert's stored (articleId, productName) and the order line agree —
 * matching on the raw articleId picked an arbitrary product on any platform shipping 'no_article_id'.
 */
function findAlertProductInOrder(
  alert: PriceAlert,
  productData: { articleId?: string; name?: string; quantity: number }[],
  platform: string
) {
  const alertIdentifier = resolveProductIdentifier(platform, alert.articleId, alert.productName);
  if (!alertIdentifier) return undefined;
  return productData.find(p => resolveProductIdentifier(platform, p.articleId, p.name) === alertIdentifier);
}

/**
 * Filters price alerts by date range based on view and cursor
 */
export function filterPriceAlertsByPeriod(
  priceAlerts: PriceAlert[],
  cursor: Date,
  view: View
): PriceAlert[] {
  if (view === 'all') return priceAlerts;

  const start = startOfPeriod(cursor, view);
  const end = new Date(cursor);

  return priceAlerts.filter(alert => {
    const alertDate = new Date(alert.toDate);

    if (view === 'day') {
      const dayEnd = new Date(end);
      dayEnd.setDate(dayEnd.getDate() + 1);
      return alertDate >= start && alertDate < dayEnd;
    } else if (view === 'week') {
      const weekEnd = new Date(start);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return alertDate >= start && alertDate < weekEnd;
    } else if (view === 'month') {
      const monthEnd = new Date(start);
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      return alertDate >= start && alertDate < monthEnd;
    }

    return true;
  });
}

/**
 * Gets the most recent price alert for a specific article in a given period
 */
/**
 * Most recent alert for a product, as a % change. `identifier` is a resolved product identity
 * (`resolveProductIdentifier`), not a raw article id — matching on the raw id merged every product on
 * a platform that ships the 'no_article_id' sentinel, and merged the products sharing a reused article
 * id (vhcjongens 45.100.13). The platform guard also stops an alert from another supplier that happens
 * to use the same article id from leaking onto this row.
 */
export function getPriceAlertForProduct(
  priceAlerts: PriceAlert[],
  identifier: string,
  platform: string,
  periodStart: Date,
  view: View
): number | undefined {
  const periodAlerts = filterPriceAlertsByPeriod(priceAlerts, periodStart, view);

  const productAlert = periodAlerts
    .filter(alert => {
      if (alert.platform && alert.platform !== platform) return false;
      return resolveProductIdentifier(alert.platform ?? platform, alert.articleId, alert.productName) === identifier;
    })
    .sort((a, b) => new Date(b.toDate).getTime() - new Date(a.toDate).getTime())[0];

  if (!productAlert) return undefined;

  const percentChange = ((productAlert.toPrice - productAlert.fromPrice) / productAlert.fromPrice) * 100;
  return percentChange
}

export interface PlatformStatistic {
  platform: string;
  totalAlerts: number;
  priceIncreases: number;
  priceDecreases: number;
  avgPriceChange: number;
  maxPriceChange: number;
  minPriceChange: number;
}

/**
 * Filter price alerts by restaurants
 * @param alerts - Array of price alerts to filter
 * @param restaurantFilters - Array of restaurant IDs to include (empty array means no filter)
 * @param platformToSectionIndex - Optional index to map platform IDs to sections for restaurant filtering
 * @returns Filtered array of price alerts
 */
export function filterPriceAlertsByRestaurants(
  alerts: PriceAlert[],
  restaurantFilters: string[],
  platformToSectionIndex?: Record<string, any>
): PriceAlert[] {
  if (restaurantFilters.length === 0) return alerts;

  return alerts.filter(alert => {
    // Use the platform-to-section index to find the restaurant
    if (platformToSectionIndex && alert.restaurantPlatformId) {
      const section = platformToSectionIndex[alert.restaurantPlatformId];
      if (section && section.restaurantId) {
        return restaurantFilters.includes(section.restaurantId);
      }
    }
    return false;
  });
}

/**
 * Filter price alerts by platforms
 * @param alerts - Array of price alerts to filter
 * @param platforms - Array of platform names to include (empty array means no filter)
 * @returns Filtered array of price alerts
 */
export function filterPriceAlertsByPlatforms(
  alerts: PriceAlert[],
  platforms: string[]
): PriceAlert[] {
  if (platforms.length === 0) return alerts;
  return alerts.filter(alert => alert.platform && platforms.includes(alert.platform));
}

/**
 * Filter price alerts by restaurant sections
 * @param alerts - Array of price alerts to filter
 * @param sectionFilters - Array of section IDs to include (empty array means no filter)
 * @param platformToSectionIndex - Index mapping platform IDs to sections
 * @returns Filtered array of price alerts
 */
export function filterPriceAlertsBySections(
  alerts: PriceAlert[],
  sectionFilters: string[],
  platformToSectionIndex?: Record<string, any>
): PriceAlert[] {
  if (sectionFilters.length === 0) return alerts;

  return alerts.filter(alert => {
    // Use the platform-to-section index to find the section
    if (platformToSectionIndex && alert.restaurantPlatformId) {
      const section = platformToSectionIndex[alert.restaurantPlatformId];
      if (section && section.id) {
        return sectionFilters.includes(section.id);
      }
    }
    return false;
  });
}

/**
 * Filter price alerts by categories
 * @param alerts - Array of price alerts to filter
 * @param categories - Array of category names to include (empty array means no filter)
 * @returns Filtered array of price alerts
 */
export function filterPriceAlertsByCategories(
  alerts: PriceAlert[],
  categories: string[]
): PriceAlert[] {
  if (categories.length === 0) return alerts;
  return alerts.filter(alert => alert.category && categories.includes(alert.category));
}

/**
 * Filter price alerts by change type (all/increases/decreases)
 * @param alerts - Array of price alerts to filter
 * @param changeType - The change type filter to apply
 * @returns Filtered array of price alerts
 */
export function filterPriceAlertsByChangeType(
  alerts: PriceAlert[],
  changeType: PriceAlertChangeType
): PriceAlert[] {
  if (changeType === 'all') return alerts;

  return alerts.filter(alert => {
    const diff = alert.toPrice - alert.fromPrice;
    if (changeType === 'increases') return diff > 0;
    if (changeType === 'decreases') return diff < 0;
    return true;
  });
}

/**
 * Get available categories from price alerts
 * @param alerts - Array of price alerts
 * @returns Sorted array of unique category names
 */
export function getAvailableCategories(alerts: PriceAlert[]): string[] {
  return Array.from(new Set(
    alerts
      .map(alert => alert.category)
      .filter((category): category is string => Boolean(category))
  )).sort();
}

/**
 * Filter price alerts by overall type (all/food/drinks/non-food)
 * Same decision as filterOrdersByOverallType (orderhistory/utils.ts): one alert is one product line,
 * so it routes straight through `resolveSpendSide`, which reads the alert's own
 * `combinedOrderingAccount` before falling back to its section type.
 * @note 'take-away' is treated as 'all' for price alerts since they don't have order source information
 * Spec: z_ralph/tasks/1_new_tasks/spec-combined-ordering-accounts.md §6
 */
export function filterPriceAlertsByOverallType(
  alerts: PriceAlert[],
  overallTypeFilter: OverallTypeFilter
): PriceAlert[] {
  if (overallTypeFilter === 'all' || overallTypeFilter === 'take-away') return alerts;

  return alerts.filter(alert => {
    // 'other' / 'dish' / 'ingredient' are revenue-side filters with no order side — they keep
    // falling through to an empty result, as before.
    if (overallTypeFilter !== 'food' && overallTypeFilter !== 'drinks' && overallTypeFilter !== 'non-food') {
      return false;
    }
    return resolveSpendSide(alert, alert.sectionType, alert.combinedOrderingAccount) === overallTypeFilter;
  });
}

/**
 * Calculate platform statistics from price alerts
 * @param alerts - Array of price alerts to analyze
 * @returns Array of platform statistics sorted by total alerts (descending)
 */
export function calculatePlatformStatistics(alerts: PriceAlert[]): PlatformStatistic[] {
  // Group alerts by platform
  const statsMap = new Map<string, {
    alerts: PriceAlert[];
    increases: number;
    decreases: number;
    changes: number[];
  }>();

  alerts.forEach(alert => {
    const platformName = alert.platform || 'Unknown';

    if (!statsMap.has(platformName)) {
      statsMap.set(platformName, { alerts: [], increases: 0, decreases: 0, changes: [] });
    }

    const stats = statsMap.get(platformName)!;
    stats.alerts.push(alert);

    const percentChange = ((alert.toPrice - alert.fromPrice) / alert.fromPrice) * 100;
    stats.changes.push(percentChange);

    if (percentChange > 0) {
      stats.increases++;
    } else if (percentChange < 0) {
      stats.decreases++;
    }
  });

  // Convert map to array of statistics
  const platformStatsArray = Array.from(statsMap.entries()).map(([platform, stats]) => ({
    platform,
    totalAlerts: stats.alerts.length,
    priceIncreases: stats.increases,
    priceDecreases: stats.decreases,
    avgPriceChange: stats.changes.length > 0
      ? stats.changes.reduce((a, b) => a + b, 0) / stats.changes.length
      : 0,
    maxPriceChange: stats.changes.length > 0 ? Math.max(...stats.changes) : 0,
    minPriceChange: stats.changes.length > 0 ? Math.min(...stats.changes) : 0,
  }));

  // Sort by total alerts descending
  return platformStatsArray.sort((a, b) => b.totalAlerts - a.totalAlerts);
}

/**
 * Calculate the actual money impact from price alerts
 * Multiplies price difference by quantity purchased for each alert
 * @param priceAlerts - Array of price alerts to calculate impact for
 * @param orderHistoryStore - Order history store to look up orders
 * @returns Total money impact in euros (positive = cost increase, negative = cost decrease)
 */
export function calculateActualMoneyImpact(
  priceAlerts: PriceAlert[],
  orderHistoryStore: OrderHistoryState
): number {
  let totalImpact = 0;
  
  for (const alert of priceAlerts) {
    if (!alert.toOrderHistoryId) continue;
    
    const order = orderHistoryStore.byId(alert.toOrderHistoryId);
    if (!order) continue;

    const platformName = alert.platform || order.platform;
    const product = findAlertProductInOrder(alert, order.productData, platformName);

    if (!product) continue;

    // Calculate price difference per unit
    const priceDifference = alert.toPrice - alert.fromPrice;
    
    // Multiply by quantity purchased
    const impact = priceDifference * product.quantity;
    
    totalImpact += impact;
  }
  
  return totalImpact;
}

/**
 * Calculate weighted average price change from price alerts
 * Weighted by quantity purchased for each product
 * @param priceAlerts - Array of price alerts to calculate weighted average for
 * @param orderHistoryStore - Order history store to look up orders
 * @returns Weighted average percentage change (0 if no valid alerts)
 */
export function calculateWeightedAveragePriceChange(
  priceAlerts: PriceAlert[],
  orderHistoryStore: OrderHistoryState
): number {
  let totalWeightedChange = 0;
  let totalQuantity = 0;
  
  for (const alert of priceAlerts) {
    if (!alert.toOrderHistoryId) continue;
    
    const order = orderHistoryStore.byId(alert.toOrderHistoryId);
    if (!order) continue;

    const platformName = alert.platform || order.platform;
    const product = findAlertProductInOrder(alert, order.productData, platformName);

    if (!product || product.quantity === 0) continue;
    
    // Calculate percentage change
    const percentChange = ((alert.toPrice - alert.fromPrice) / alert.fromPrice) * 100;
    
    // Add to weighted sum: percentage * quantity
    totalWeightedChange += percentChange * product.quantity;
    totalQuantity += product.quantity;
  }
  
  // Return weighted average: sum(percentage * quantity) / sum(quantity)
  return totalQuantity > 0 ? totalWeightedChange / totalQuantity : 0;
}
